<script lang="ts">
    import tabIcon from "@/assets/gear.svg";

    export default {
        name: "ListKontaktorsak",
        tabName: "Kontaktorsak",
        icon: tabIcon,
    };
</script>

<script setup lang="ts">
    import KontaktorsakForm from "@/tabs/Admin/forms/KontaktorsakForm.vue";
    import { ref, onBeforeUnmount, onBeforeMount, computed, toRefs, onMounted } from "vue";
    import emitter from "tiny-emitter/instance";
    import KlinikenTable from "@/components/Table/KlinikenTable.vue";
    import CustomButton from "@/components/Button.vue";
    import { openDialog } from "@/utils";
    import AdminLockBorder from "@/components/AdminLockBorder.vue";
    import { useStore } from "@/store";
    import CollapsibleSection from "@/components/generic/CollapsibleSection.vue";

    interface Kontaktorsak {
        id: number;
        namn: string;
    }

    const store = useStore();

    const props = defineProps<{
        tabId: number;
    }>();

    const columns = [
        {
            title: "Namn",
            name: "displayName",
            sort: "namn",
        },
    ];

    const dropdownActions: {
        name: string;
        title: string;
        show: () => boolean;
    }[] = [
        {
            name: "editKontaktorsak",
            title: "Redigera kontaktorsak",
            show: () => true,
        },
    ];

    const listPage = ref<number>(1);
    const listCount = ref<number>(0);
    const listLimit = ref<number>(20);
    const filterParams = ref<{ [key: string]: string } | undefined>();
    const subsetName = ref<string>("default");

    const kontaktorsaker = computed<Kontaktorsak[]>(() =>
        store.getters["operation/kontaktorsak/kontaktorsakSubset"]({
            subsetName: subsetName.value,
            offset: listLimit.value * (listPage.value - 1),
            limit: listLimit.value,
        })
    );

    const kontaktorsakernewlycreated = computed<Kontaktorsak[]>(() =>
        store.getters["operation/kontaktorsak/kontaktorsakSubset"]({
            subsetName: "newlyCreated",
            offset: listLimit.value * (listPage.value - 1),
            limit: listLimit.value,
        })
    );

    const { tabId } = toRefs(props);

    defineExpose({
        tabId,
    });

    const onChangeLimit = async (newLimit: number) => {
        listLimit.value = newLimit;
        await fetchKontaktorsaker({ limit: newLimit, page: 1 });
    };

    const onLoadPage = async (page: number) => {
        await fetchKontaktorsaker({ page });
    };

    const openCreateKontaktorsak = (kontaktorsak: Kontaktorsak) => {
        store.dispatch("tabs/openTab", {
            component: KontaktorsakForm,
            parameters: { kontaktorsakId: kontaktorsak.id },
        });
    };

    const openEditKontaktorsak = (kontaktorsak: Kontaktorsak) => {
        store.dispatch("tabs/openTab", {
            component: KontaktorsakForm,
            parameters: { kontaktorsakId: kontaktorsak.id },
        });
    };

    const onSortChange = async (newSortBy: string) => {
        filterParams.value = {
            ...(filterParams.value ? filterParams.value : {}),
            ordering: newSortBy,
        };
        await fetchKontaktorsaker({ page: 1 });
    };

    const fetchKontaktorsaker = async ({ page, limit = listLimit.value }: { page: number; limit?: number }) => {
        try {
            const {
                nextPage,
                total,
                subsetName: fetchedSubsetName,
            } = await store.dispatch("operation/kontaktorsak/fetchKontaktorsak", {
                page,
                limit,
                filterParams: filterParams.value,
            });

            listPage.value = page;
            subsetName.value = fetchedSubsetName;

            if (total) {
                listCount.value = total;
            }
        } catch (_) {
            console.log(_);
            openDialog("Kunde inte hämta kontaktorsaker", "error");
        }
    };

    onMounted(async () => {
        await fetchKontaktorsaker({ page: listPage.value });
    });
</script>

<template>
    <div class="list-kontaktorsak">
        <admin-lock-border />
        <div class="header">
            <h1>Kontaktorsaker</h1>
            <CustomButton type="button" variant="hollow" @click="openCreateKontaktorsak" :style="{ padding: 0 }">
                <img src="@/assets/plus_circle_blue.svg" />
                <span>Skapa kontaktorsak</span>
            </CustomButton>
        </div>
        <div class="newly-created" v-if="kontaktorsakernewlycreated.length > 0">
            <collapsible-section isSingle :isExpanded="false" heading="Nyligen tillagda kontaktorsaker">
                <kliniken-table
                    :items="kontaktorsakernewlycreated"
                    :columns="columns"
                    @editKontaktorsak="openEditKontaktorsak"
                    :has-dropdown="true"
                    :dropdown-actions="dropdownActions"
                />
            </collapsible-section>
        </div>
        <div>
            <kliniken-table
                :items="kontaktorsaker"
                :columns="columns"
                :count="listCount"
                :currentPage="listPage"
                :limit="listLimit"
                paginationStyle="paging"
                @sort="onSortChange"
                @loadPage="onLoadPage"
                @changeLimit="onChangeLimit"
                @editKontaktorsak="openEditKontaktorsak"
                :showBottom="true"
                :showLimitDropdown="true"
                :has-dropdown="true"
                :dropdown-actions="dropdownActions"
            />
        </div>
    </div>
</template>

<style scoped lang="scss">
    .list-kontaktorsak {
        .header {
            h1 {
                margin: 0;
            }
            display: flex;
            gap: 20px;
            align-items: center;
        }
    }
</style>
